import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Labeled numeric cell used in leg cards + account-pair summaries.
 * When `live={true}` the label is tinted accent-pink and carries a
 * small filled dot — signal to the operator that this number is
 * coming from the HedgePair WS (position:update / account:tick) and
 * not the REST snapshot that loaded the page.
 */
export function LiveCell({
  label,
  value,
  color,
  live,
}: {
  label: string;
  value: string;
  color?: string;
  live?: boolean;
}): ReactNode {
  return (
    <div className="min-w-0">
      <p
        className={cn(
          "truncate text-[10px] uppercase tracking-wider text-muted-foreground",
          live && "text-accent/80",
        )}
      >
        {label}
        {live && <span className="ml-1 text-accent">●</span>}
      </p>
      <p
        className={cn(
          "mt-0.5 truncate font-mono text-sm font-semibold tabular-nums",
          color,
        )}
        title={value}
      >
        {value}
      </p>
    </div>
  );
}

/**
 * Three-state dot reflecting the HedgePair WS status for a given
 * pair: grey (disconnected) → amber (handshake in flight) → emerald
 * (subscribed + receiving). Paired with a compact caption so operators
 * can tell at a glance whether the numbers on-screen are ticking.
 */
export function LiveDot({
  status,
  showLabel = true,
}: {
  status: "connecting" | "open" | "closed";
  /** Render the uppercase LIVE / CONNECTING / OFFLINE caption next to
   *  the dot. Set false in dense layouts (card headers) where the dot
   *  alone is enough and the caption would crowd the row. */
  showLabel?: boolean;
}): ReactNode {
  const cls =
    status === "open"
      ? "bg-emerald-500"
      : status === "connecting"
        ? "bg-amber-400"
        : "bg-muted-foreground/40";
  const label =
    status === "open"
      ? "Live"
      : status === "connecting"
        ? "Connecting"
        : "Offline";
  return (
    <span className="inline-flex items-center gap-1.5" title={label}>
      <span className={cn("h-2 w-2 rounded-full", cls)} />
      {showLabel && (
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
      )}
    </span>
  );
}
